import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Thread } from "./Thread";
import { ANDREA_PHONE, ANDREA_PHONE_DISPLAY, contactClick } from "../lib/contact";
import { trackEvent } from "../lib/tracking";
import { STAGE_CONTENT } from "../pages/clientJourney";

// /schedule-sleep-plan-review — the post-intake chapter. Parents arrive from
// the "I've Submitted My Intake" tap or their Intake Received email. The
// dedicated 45-minute Sleep Plan Review Calendly event (never the free
// assessment) is delivered by the verified /api/pay/intake lookup; Andrea's
// ~5-day preparation buffer is enforced by the event's own minimum notice.
const API = process.env.REACT_APP_BACKEND_URL;

export const ScheduleReview = () => {
  const reduce = useReducedMotion();
  const [state, setState] = useState(null); // null = resolving | "unknown" | data
  const sessionId = new URLSearchParams(window.location.search).get("session_id");

  useEffect(() => {
    document.title = "Schedule your Sleep Plan Review — The Nurtured Parent Co.";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    window.scrollTo(0, 0);

    let cancelled = false;
    (async () => {
      if (!sessionId) {
        if (!cancelled) setState("unknown");
        return;
      }
      try {
        const resp = await fetch(`${API}/api/pay/intake`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!resp.ok) throw new Error(`lookup failed: ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setState(data);
      } catch {
        if (!cancelled) setState("unknown");
      }
    })();

    return () => {
      cancelled = true;
      document.head.removeChild(meta);
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  const intakePending = state && state !== "unknown" && state.intake_status !== "complete";
  const backRoute = intakePending && state.service_type && state.client_stage
    ? `/${state.service_type}-${state.client_stage}?session_id=${sessionId}`
    : null;
  const stageInfo = state && state !== "unknown" && state.client_stage ? STAGE_CONTENT[state.client_stage] : null;

  return (
    <div data-testid="schedule-review-page" className="min-h-svh bg-ivory text-ink flex flex-col">
      <header className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-8">
        <a href="/" data-testid="schedule-logo" className="inline-block">
          <span className="block font-serif font-light text-xl sm:text-2xl leading-tight tracking-tight">
            The Nurtured Parent Co.
          </span>
          <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
            Pediatric Sleep Consulting
          </span>
        </a>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20">
        <section className="py-16 lg:py-24">
          <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
            {state === "unknown" ? "Sleep Plan Review" : "Intake received"}
          </motion.p>
          <motion.h1 {...rise(0.25)} className="mt-6 font-serif font-light text-ink text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight">
            {state === "unknown" ? (
              <>One step <span className="italic text-clay">left.</span></>
            ) : (
              <>You&rsquo;re <span className="italic text-clay">all set.</span></>
            )}
          </motion.h1>
          {state !== "unknown" && (
          <motion.div {...rise(0.4)} className="mt-10 max-w-lg space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
            <p>
              Andrea now has the information she needs to begin reviewing your
              child&rsquo;s sleep and preparing your personalized
              recommendations.
            </p>
            <p>
              Your intake is complete. Andrea will review everything before
              your call — so your time together can focus on your child, your
              plan, and your questions.
            </p>
          </motion.div>
          )}
        </section>

        <section className="py-16 lg:py-24 border-t border-ink/10">
          <motion.div {...rise(0.1)} className="max-w-2xl">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              The last step
            </p>
            <h2 className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
              Schedule your 45-minute
              <span className="block italic text-clay">Sleep Plan Review.</span>
            </h2>

            {intakePending && backRoute && (
              <div data-testid="schedule-intake-first" className="mt-8 max-w-xl rounded-3xl border border-ink/10 bg-linen/60 p-6">
                <p className="text-base font-normal leading-relaxed text-smoke">
                  One step first — your {stageInfo ? stageInfo.intakeLabel.toLowerCase() : "intake"} isn&rsquo;t
                  marked complete yet. Finish it and tap &ldquo;I&rsquo;ve Submitted My
                  Intake&rdquo; on your welcome page, then come right back here.
                </p>
                <a
                  data-testid="back-to-welcome-link"
                  href={backRoute}
                  className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300"
                >
                  Back to My Intake
                  <ArrowRight size={16} />
                </a>
              </div>
            )}

            {!intakePending && (
              <>
                <p className="mt-6 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-xl">
                  Thank you for completing your intake! Your next step is to
                  schedule your 45-minute call with Andrea to review your
                  personalized sleep plan. Andrea sets aside a few days between
                  your intake and your call — that&rsquo;s her review and
                  preparation time, and it&rsquo;s what makes the conversation
                  about your child from the first minute. Choose the first time
                  that genuinely works for you.
                </p>
                {state === null && (
                  <p data-testid="schedule-loading" className="mt-8 text-base font-normal text-smoke">
                    One moment…
                  </p>
                )}
                {state && state !== "unknown" && state.plan_review_url && (
                  <a
                    data-testid="schedule-review-cta"
                    href={state.plan_review_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent("sleep_plan_review_booking_started", {
                        service_type: state.service_type,
                        client_stage: state.client_stage,
                      })
                    }
                    className="mt-9 w-full sm:w-auto justify-center inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-10 py-5 text-lg hover:bg-espresso transition-colors duration-300 group"
                  >
                    Schedule My 45-Minute Call
                    <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                )}
                {state === "unknown" && (
                  <p data-testid="schedule-find-email" className="mt-8 text-base font-normal leading-relaxed text-smoke max-w-xl">
                    Your scheduling link arrived in your &ldquo;We received your
                    intake&rdquo; email — it keeps everything connected to your
                    family&rsquo;s file. Can&rsquo;t find it? Text Andrea and
                    she&rsquo;ll send it over.
                  </p>
                )}
              </>
            )}

            <p className="mt-8 text-sm font-normal leading-relaxed text-smoke max-w-xl">
              Rather talk it through?{" "}
              <a
                data-testid="schedule-text-andrea"
                href={`sms:${ANDREA_PHONE}`}
                onClick={(e) => {
                  trackEvent("text_andrea_click", { location: "schedule_review" });
                  contactClick("text")(e);
                }}
                className="text-ink underline underline-offset-4 decoration-ink/25 hover:decoration-clay transition-[text-decoration-color] duration-300"
              >
                Text Andrea — {ANDREA_PHONE_DISPLAY}
              </a>
            </p>
          </motion.div>
        </section>
      </main>

      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 pb-10">
        <Thread className="text-clay/40" />
      </div>
    </div>
  );
};
